import { type MutationFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { CsvIngestionStatusEnum, CustomErpCsvUploadResponse } from 'api/types/csvIngestionTypes'
import { Transaction } from 'api/types/transaction/transactionApiTypes'
import useApi from 'api/useApi'
import { ResponseError } from 'api/utils/useFetchWithAuth'
import { CUSTOM_ERP_MAPPING } from 'constants/AccountingApp'
import Permission from 'constants/Permissions'
import { useUser } from 'contexts/UserContext'
import { FIVE_MIN, ONE_MIN } from 'hooks/data/dataTimeouts'
import { useOptimisticPendingChanges } from 'hooks/data/gc-dags/useOptimisticPendingChanges'
import { QueryKey } from 'hooks/data/queryKeys'
import useFeatureFlags from 'hooks/feature-flags/useFeatureFlags'
import { useTransactionFilterParams } from 'hooks/paginatedTransactionsHook'
import { useOrgQueryKey } from 'hooks/utils/useOrgQueryKey'

export function useAccountingAppStatus() {
  const { fetch_erp_status } = useApi()
  const { enableGrandCanyonErpSync } = useFeatureFlags()
  const erpStatusQueryKey = useOrgQueryKey(QueryKey.ErpStatus)

  const {
    isLoading,
    isError,
    isFetching,
    data: connection,
  } = useQuery(erpStatusQueryKey, fetch_erp_status, {
    staleTime: 100,
    enabled: enableGrandCanyonErpSync,
  })

  return {
    isLoading: enableGrandCanyonErpSync && isLoading,
    isError: enableGrandCanyonErpSync && isError,
    isFetching: enableGrandCanyonErpSync && isFetching,
    status:
      enableGrandCanyonErpSync && !isLoading && !isError
        ? connection.erp_accounting_app
        : undefined,
  }
}

export const isCustomCoaEnabled = (accountingApp?: string | null | false) =>
  accountingApp === CUSTOM_ERP_MAPPING

export function useAccountingAppData() {
  const {
    fetch_org_settings,
    fetch_erp_status,
    fetch_erp_accounts,
    fetch_erp_classes,
    fetch_erp_customers,
    fetch_erp_locations,
    fetch_erp_vendors,
    fetch_accounting_mappings,
    fetch_sync_doc_number_prefix,
    fetch_custom_erp_mapping_enabled_setting,
  } = useApi()
  const { enableGrandCanyonErpSync } = useFeatureFlags()
  const orgSettingsQueryKey = useOrgQueryKey(QueryKey.OrgSettings)
  const erpStatusQueryKey = useOrgQueryKey(QueryKey.ErpStatus)
  const erpAcountsQueryKey = useOrgQueryKey(QueryKey.ErpAccounts)
  const erpCustomersQueryKey = useOrgQueryKey(QueryKey.ErpCustomers)
  const erpLocationsQueryKey = useOrgQueryKey(QueryKey.ErpLocations)
  const erpVendorsQueryKey = useOrgQueryKey(QueryKey.ErpVendors)
  const erpClassesQueryKey = useOrgQueryKey(QueryKey.ErpClasses)
  const accountingMappingsQueryKey = useOrgQueryKey(QueryKey.AccountingMappings)
  const accountingDocNumberPrefixQueryKey = useOrgQueryKey(QueryKey.AccountingDocNumberPrefix)
  const customErpMappingQueryKey = useOrgQueryKey(QueryKey.CustomErpMappingEnabled)

  const { hasPermission } = useUser()
  const userHasPermission = hasPermission(Permission.SettingOrgRead)

  const {
    isLoading: isSettingsLoading,
    isError: isSettingsError,
    data: settings,
  } = useQuery(orgSettingsQueryKey, fetch_org_settings, {
    staleTime: FIVE_MIN,
    enabled: userHasPermission,
  })

  const {
    isLoading: isStatusLoading,
    isError: isStatusLoadingError,
    data: status,
  } = useQuery(erpStatusQueryKey, fetch_erp_status, {
    staleTime: 100,
    enabled: enableGrandCanyonErpSync,
  })

  const {
    isLoading: isAccountsLoading,
    isError: isAccountsLoadingError,
    data: accounts,
  } = useQuery(erpAcountsQueryKey, fetch_erp_accounts, {
    staleTime: ONE_MIN,
    enabled: enableGrandCanyonErpSync,
  })

  const {
    isLoading: isCustomersLoading,
    isError: isCustomersError,
    data: customers,
  } = useQuery(erpCustomersQueryKey, fetch_erp_customers, {
    enabled: enableGrandCanyonErpSync,
  })

  const {
    isLoading: isVendorsLoading,
    isError: isVendorsError,
    data: vendors,
  } = useQuery(erpVendorsQueryKey, fetch_erp_vendors, {
    enabled: enableGrandCanyonErpSync,
  })

  const {
    isLoading: isClassesLoading,
    isError: isClassesError,
    data: classes,
  } = useQuery(erpClassesQueryKey, fetch_erp_classes, {
    enabled: enableGrandCanyonErpSync,
  })

  const {
    isLoading: isLocationsLoading,
    isError: isLocationsError,
    data: locations,
  } = useQuery(erpLocationsQueryKey, fetch_erp_locations, {
    enabled: enableGrandCanyonErpSync,
  })

  const {
    isLoading: isMappingsLoading,
    isError: isMappingsLoadingError,
    data: mappings,
  } = useQuery(accountingMappingsQueryKey, fetch_accounting_mappings, {
    staleTime: FIVE_MIN,
    enabled: enableGrandCanyonErpSync,
  })

  const {
    isLoading: isDocPrefixLoading,
    isError: isDocPrefixLoadingError,
    data: docPrefix,
  } = useQuery(accountingDocNumberPrefixQueryKey, fetch_sync_doc_number_prefix, {
    enabled: enableGrandCanyonErpSync,
  })

  const {
    isLoading: isNetsuiteCsvEnabledLoading,
    isError: isNetsuiteCsvEnabledError,
    data: isNetsuiteCsvEnabled,
  } = useQuery(customErpMappingQueryKey, fetch_custom_erp_mapping_enabled_setting, {
    enabled: enableGrandCanyonErpSync,
  })

  return {
    isLoading:
      isSettingsLoading ||
      isStatusLoading ||
      isAccountsLoading ||
      isMappingsLoading ||
      isCustomersLoading ||
      isVendorsLoading ||
      isClassesLoading ||
      isLocationsLoading ||
      isNetsuiteCsvEnabledLoading,
    isError:
      isSettingsError ||
      isStatusLoadingError ||
      isAccountsLoadingError ||
      isMappingsLoadingError ||
      isCustomersError ||
      isVendorsError ||
      isClassesError ||
      isLocationsError ||
      isNetsuiteCsvEnabledError,
    connectedToErp: !isStatusLoading && !isStatusLoadingError && status.connected,
    hasAccountingMinimumValidMapping:
      !isStatusLoading && !isStatusLoadingError && status.has_valid_mapping,
    latestAccountingSyncTimestamp:
      !isStatusLoading && !isStatusLoadingError && status.latest_sync_timestamp,
    connectionTimestamp: !isStatusLoading && !isStatusLoadingError && status.connected_timestamp,
    chartOfAccounts: !isAccountsLoadingError && !isAccountsLoading ? accounts : {},
    customers: !isCustomersError && !isCustomersLoading ? customers : {},
    vendors: !isVendorsError && !isVendorsLoading ? vendors : {},
    classes: !isClassesError && !isClassesLoading ? classes : {},
    locations: !isLocationsError && !isLocationsLoading ? locations : {},
    mappings: !isMappingsLoadingError && !isMappingsLoading ? mappings : {},
    docNumberPrefix: !isDocPrefixLoadingError && !isDocPrefixLoading ? docPrefix.result : '',
    isNetsuiteCsvEnabled:
      !isNetsuiteCsvEnabledError && !isNetsuiteCsvEnabledLoading && isNetsuiteCsvEnabled,
    classLocationEnabled: !isSettingsError && !isSettingsLoading && settings.class_location_support,
    accountingApp: status?.erp_accounting_app,
    companies: status?.company_info || [],
  }
}

export function useCustomCoaStatus() {
  const { isError, isLoading, status } = useAccountingAppStatus()

  return {
    isCustomCoaEnabled: !isLoading && !isError && isCustomCoaEnabled(status),
    isLoading,
    isError,
  }
}

export function useAccountingMappingsCsv() {
  const { fetch_accounting_mappings_csv } = useApi()
  const accountingMappingsCsvQueryKey = useOrgQueryKey(QueryKey.AccountingMappingsCsv)

  return useQuery(accountingMappingsCsvQueryKey, fetch_accounting_mappings_csv, {
    staleTime: FIVE_MIN,
  })
}

export function useUpdateCustomErpMappingEnabledMutation() {
  const { update_custom_erp_mapping_enabled_setting } = useApi()
  const queryClient = useQueryClient()
  const { setPendingChangeFlag } = useOptimisticPendingChanges()

  return useMutation(
    (isCustomErpMappingEnabled: boolean) =>
      update_custom_erp_mapping_enabled_setting(isCustomErpMappingEnabled),
    {
      onSuccess(_data, isCustomErpMappingEnabled) {
        void queryClient.invalidateQueries([QueryKey.CustomErpMappingEnabled])
        void queryClient.invalidateQueries([QueryKey.ErpStatus])
        if (!isCustomErpMappingEnabled) {
          setPendingChangeFlag('mapping_delete')
        }
      },
    }
  )
}

export function useInvalidateAccountingAppData() {
  const queryClient = useQueryClient()

  return () => {
    void queryClient.invalidateQueries([QueryKey.ErpStatus])
    void queryClient.invalidateQueries([QueryKey.ErpAccounts])
    void queryClient.invalidateQueries([QueryKey.AccountingMappings])
    void queryClient.invalidateQueries([QueryKey.CustomErpMappingEnabled])
  }
}

type UseAccountingAppMutationOptions<TData, TError> = {
  onSuccess?: (data: TData) => void
  onError?: (error: TError) => void
  setPendingChangeMappingUpsert?: boolean
  setPendingChangeMappingDelete?: boolean
}

export function useAccountingAppMutation<TData = unknown, TError = unknown, TVariables = undefined>(
  mutationFunc: MutationFunction<TData, TVariables>,
  options?: UseAccountingAppMutationOptions<TData, TError>
) {
  const queryClient = useQueryClient()
  const { setPendingChangeFlag } = useOptimisticPendingChanges()

  return useMutation<TData, TError, TVariables>(mutationFunc, {
    onError: (error) => {
      options?.onError?.(error)
    },

    onSuccess(data) {
      options?.onSuccess?.(data)

      void queryClient.invalidateQueries([QueryKey.ErpStatus])
      void queryClient.invalidateQueries([QueryKey.ErpAccounts])
      void queryClient.invalidateQueries([QueryKey.AccountingMappings])
      void queryClient.invalidateQueries([QueryKey.AccountingDocNumberPrefix])
      void queryClient.invalidateQueries([QueryKey.CustomErpMappingEnabled])
      void queryClient.invalidateQueries([QueryKey.AccountingSyncStatuses])
      void queryClient.invalidateQueries([QueryKey.AccountingSyncStatusesForFilters])

      if (options?.setPendingChangeMappingUpsert) {
        setPendingChangeFlag('mapping_upsert')
      }

      if (options?.setPendingChangeMappingDelete) {
        setPendingChangeFlag('mapping_delete')
      }
    },
  })
}

export function useDisconnectAccountingErpMutation() {
  const { disconnect_accounting_erp } = useApi()

  const queryClient = useQueryClient()
  return useMutation(disconnect_accounting_erp, {
    onSuccess() {
      void queryClient.invalidateQueries([QueryKey.ErpStatus])
      void queryClient.invalidateQueries([QueryKey.ErpAccounts])
      void queryClient.invalidateQueries([QueryKey.AccountingMappings])
      void queryClient.invalidateQueries([QueryKey.AccountingDocNumberPrefix])
      void queryClient.invalidateQueries([QueryKey.CustomErpMappingEnabled])
      void queryClient.invalidateQueries([QueryKey.AccountingSyncStatuses])
      void queryClient.invalidateQueries([QueryKey.AccountingSyncStatusesForFilters])
    },
  })
}

export function useUpdateSyncDocNumberPrefixMutation(options?: {
  onSuccess?: ({ result }: { result: string }) => void
  onError?: (error: unknown) => void
}) {
  const { update_sync_doc_number_prefix } = useApi()

  return useAccountingAppMutation(
    ({ syncDocNumberPrefix }: { syncDocNumberPrefix: string }) =>
      update_sync_doc_number_prefix(syncDocNumberPrefix),
    options
  )
}

export function useUpsertAccountingMappingMutation() {
  const { upsert_accounting_mapping } = useApi()

  return useAccountingAppMutation(
    ({
      mapping,
      mappingType,
      mappingQualifier,
      mappingQualifiers,
    }: {
      mapping: unknown
      mappingType: string
      mappingQualifier: string | null
      mappingQualifiers?: string[] | null
    }) => upsert_accounting_mapping(mapping, mappingType, mappingQualifier, mappingQualifiers),
    { setPendingChangeMappingUpsert: true }
  )
}

export function useErpSync() {
  const { post_erp_syncs } = useApi()

  return useAccountingAppMutation(
    ({
      transactions,
      isAggregate,
      isResync,
    }: {
      transactions: Transaction[]
      isAggregate: boolean
      isResync: boolean
    }) => {
      const transactionGroupIdAndDates = transactions.map((txn) => {
        return {
          transaction_group_id: txn.groupId,
          transaction_date: txn.timestamp,
        }
      })

      return post_erp_syncs({
        transaction_group_ids_and_dates: transactionGroupIdAndDates,
        is_aggregate: isAggregate,
        is_resync: isResync,
      })
    }
  )
}

export function useErpSyncSelectAll() {
  const filters = useTransactionFilterParams()
  const { post_erp_syncs } = useApi()

  return useAccountingAppMutation(
    ({ isAggregate, isResync }: { isAggregate: boolean; isResync: boolean }) => {
      return post_erp_syncs({
        filters,
        is_aggregate: isAggregate,
        is_resync: isResync,
      })
    }
  )
}

export function useErpSyncUpdate() {
  const { patch_erp_syncs } = useApi()

  return useAccountingAppMutation(
    (batchSyncedTransactionsByJournalId: Record<string, Array<Record<string, string>>>) => {
      return patch_erp_syncs(batchSyncedTransactionsByJournalId)
    }
  )
}

export const useGetErpAccountsCsv = () => {
  const { get_erp_accounts_csv } = useApi()

  return useQuery([QueryKey.ErpAccounts], get_erp_accounts_csv)
}

export const useGetErpAccountsCsvByName = (csvName: string) => {
  const { get_erp_accounts_csv_by_name } = useApi()

  return useQuery([QueryKey.ErpAccounts, csvName], () => get_erp_accounts_csv_by_name(csvName), {
    enabled: !!csvName && csvName.length > 0,
  })
}

export const useUploadCustomErpCsv = () => {
  const { upload_erp_accounts_csv } = useApi()
  const queryClient = useQueryClient()

  return useMutation<CustomErpCsvUploadResponse, ResponseError, FormData>(
    (formData: FormData) => upload_erp_accounts_csv(formData),
    {
      onSuccess: (response) => {
        // Only invalidate queries if the upload was successful (not malformed)
        if (response.status !== CsvIngestionStatusEnum.Malformed) {
          void queryClient.invalidateQueries([QueryKey.ErpAccounts])
        }
      },
    }
  )
}
